import { useState } from 'react';
import { Check, Pencil, Plus, Trash2 } from 'lucide-react';
import { Button, Skeleton, useUndo } from '@/design/components';
import { errorMessage } from '@/lib/errors';
import {
  useQuestionMutations,
  useQuestions,
  type AdminQuestion,
  type QuestionDraft,
} from './api';

const FIELD = 'min-h-11 w-full rounded-sm border border-muted bg-surface px-3 text-body text-ink';

/**
 * עריכת השאלות של חידון, לפי מזהה החידון בלבד.
 *
 * רכיב משותף, כי חידון תלוי בלומדה וחידון של "הכר את המוצר" הם אותו
 * דבר מבחינת השאלות. ההבדל היחיד הוא מי מחזיק את החידון, וזה כבר לא
 * עניינו של המסך הזה.
 */
export function QuestionsEditor({ quizId }: { quizId: string }) {
  const undo = useUndo();
  const questions = useQuestions(quizId);
  const { saveQuestion, removeQuestion } = useQuestionMutations(quizId);
  const [editing, setEditing] = useState<QuestionDraft | null>(null);
  const [error, setError] = useState<string | null>(null);

  const run = (action: () => Promise<unknown>) => {
    setError(null);
    void action().catch((caught) => setError(errorMessage(caught)));
  };

  return (
    <div className="grid gap-3">
      {error && (
        <p role="alert" className="m-0 text-body-sm text-bad">
          {error}
        </p>
      )}

      {questions.isPending && <Skeleton className="h-24" />}

      {questions.data?.length === 0 && !editing && (
        <p className="m-0 text-body-sm text-muted">אין עדיין שאלות בחידון.</p>
      )}

      {questions.data?.map((question, i) => (
        <QuestionRow
          key={question.id}
          index={i}
          question={question}
          onEdit={() =>
            setEditing({
              id: question.id,
              prompt: question.prompt,
              explanation: question.explanation,
              options: question.quiz_options.map((option) => ({
                id: option.id,
                text: option.text,
                is_correct: option.is_correct,
              })),
            })
          }
          onRemove={() =>
            undo.remove({
              id: question.id,
              message: 'השאלה נמחקה',
              perform: () => removeQuestion.mutateAsync(question.id),
            })
          }
        />
      ))}

      {editing ? (
        <QuestionForm
          draft={editing}
          onCancel={() => setEditing(null)}
          onSave={(draft) =>
            run(async () => {
              await saveQuestion.mutateAsync(draft);
              setEditing(null);
            })
          }
        />
      ) : (
        <div>
          <Button
            onClick={() =>
              setEditing({
                prompt: '',
                explanation: null,
                options: [
                  { text: '', is_correct: true },
                  { text: '', is_correct: false },
                  { text: '', is_correct: false },
                ],
              })
            }
          >
            <Plus size={20} strokeWidth={1.75} aria-hidden="true" />
            שאלה חדשה
          </Button>
        </div>
      )}
    </div>
  );
}

function QuestionRow({
  index,
  question,
  onEdit,
  onRemove,
}: {
  index: number;
  question: AdminQuestion;
  onEdit: () => void;
  onRemove: () => void;
}) {
  return (
    <div className="grid gap-1 rounded-md border border-line p-3">
      <div className="flex flex-wrap items-center gap-2">
        <span className="w-6 shrink-0 tabular text-muted">{index + 1}</span>
        <span className="grow text-body">{question.prompt}</span>
        <Button variant="ghost" aria-label="עריכה" onClick={onEdit}>
          <Pencil size={18} strokeWidth={1.75} aria-hidden="true" />
        </Button>
        <Button variant="ghost" aria-label="מחיקה" onClick={onRemove}>
          <Trash2 size={18} strokeWidth={1.75} aria-hidden="true" />
        </Button>
      </div>
      <ul className="m-0 grid list-none gap-0.5 ps-8 pe-0 py-0">
        {question.quiz_options.map((option) => (
          <li key={option.id} className="flex items-center gap-1 text-body-sm text-muted">
            {option.is_correct && (
              <Check size={14} strokeWidth={2} aria-hidden="true" className="text-good" />
            )}
            {option.text}
            {option.is_correct && <span className="text-label text-good">נכונה</span>}
          </li>
        ))}
      </ul>
    </div>
  );
}

function QuestionForm({
  draft,
  onSave,
  onCancel,
}: {
  draft: QuestionDraft;
  onSave: (draft: QuestionDraft) => void;
  onCancel: () => void;
}) {
  const [prompt, setPrompt] = useState(draft.prompt);
  const [explanation, setExplanation] = useState(draft.explanation ?? '');
  const [options, setOptions] = useState(draft.options);
  const [warning, setWarning] = useState<string | null>(null);

  const setOption = (index: number, patch: Partial<(typeof options)[number]>) =>
    setOptions(options.map((option, i) => (i === index ? { ...option, ...patch } : option)));

  // תשובה נכונה אחת בלבד: submit_answer בודק מול is_correct יחיד
  const pickCorrect = (index: number) =>
    setOptions(options.map((option, i) => ({ ...option, is_correct: i === index })));

  return (
    <form
      className="grid gap-3 rounded-md border border-olive bg-olive-soft p-3"
      onSubmit={(event) => {
        event.preventDefault();
        const filled = options.filter((option) => option.text.trim());
        if (!prompt.trim()) return;
        if (filled.length < 2) {
          setWarning('שאלה צריכה לפחות שתי תשובות.');
          return;
        }
        if (!filled.some((option) => option.is_correct)) {
          setWarning('צריך לסמן איזו תשובה נכונה.');
          return;
        }
        onSave({
          id: draft.id,
          prompt: prompt.trim(),
          explanation: explanation.trim() || null,
          options: filled.map((option) => ({ ...option, text: option.text.trim() })),
        });
      }}
    >
      <div className="grid gap-1">
        <label htmlFor="prompt" className="text-body-sm text-muted">
          השאלה
        </label>
        <input
          id="prompt"
          value={prompt}
          onChange={(event) => setPrompt(event.target.value)}
          className={FIELD}
        />
      </div>

      <fieldset className="m-0 grid gap-2 border-0 p-0">
        <legend className="mb-1 p-0 text-body-sm text-muted">
          תשובות. לסמן את הנכונה.
        </legend>
        {options.map((option, i) => (
          <div key={i} className="flex items-center gap-2">
            <input
              type="radio"
              name="correct"
              checked={option.is_correct}
              onChange={() => pickCorrect(i)}
              aria-label={`תשובה ${i + 1} היא הנכונה`}
              className="size-5 shrink-0"
            />
            <input
              value={option.text}
              onChange={(event) => setOption(i, { text: event.target.value })}
              aria-label={`תשובה ${i + 1}`}
              className={FIELD}
            />
            {options.length > 2 && (
              <Button
                variant="ghost"
                aria-label={`מחיקת תשובה ${i + 1}`}
                onClick={() => setOptions(options.filter((_, index) => index !== i))}
              >
                <Trash2 size={18} strokeWidth={1.75} aria-hidden="true" />
              </Button>
            )}
          </div>
        ))}
        <div>
          <Button onClick={() => setOptions([...options, { text: '', is_correct: false }])}>
            <Plus size={20} strokeWidth={1.75} aria-hidden="true" />
            עוד תשובה
          </Button>
        </div>
      </fieldset>

      <div className="grid gap-1">
        <label htmlFor="explanation" className="text-body-sm text-muted">
          הסבר שמוצג אחרי התשובה
        </label>
        <textarea
          id="explanation"
          rows={2}
          value={explanation}
          onChange={(event) => setExplanation(event.target.value)}
          className={`${FIELD} py-2`}
        />
      </div>

      {warning && (
        <p role="alert" className="m-0 text-body-sm text-bad">
          {warning}
        </p>
      )}

      <div className="flex flex-wrap gap-2">
        <Button variant="primary" type="submit">
          שמירת השאלה
        </Button>
        <Button onClick={onCancel}>ביטול</Button>
      </div>
    </form>
  );
}
